﻿import { defineStore } from 'pinia'

const LOCAL_HOSTS = new Set(['', 'localhost', '127.0.0.1', '::1'])

function normalizeHostname(hostname: string): string {
  return hostname
    .trim()
    .toLowerCase()
    .replace(/^v2\./, '')
    .replace(/^staging\./, '')
}

function resolveEnvironment(rawHostname: string): 'development' | 'staging' | 'production' {
  const hostname = rawHostname.trim().toLowerCase()

  if (LOCAL_HOSTS.has(hostname)) {
    return 'development'
  }

  if (hostname.includes('staging') || hostname.endsWith('.azurestaticapps.net')) {
    return 'staging'
  }

  return 'production'
}

function getDevEmulatedHost(): string {
  const envOverride = (import.meta.env.VITE_DEV_EMULATED_HOST || '').trim()

  if (envOverride.length > 0 && LOCAL_HOSTS.has(window.location.hostname.trim().toLowerCase())) {
    return envOverride
  }

  return ''
}

function getClientRawHostname(): string {
  try {
    const override = localStorage.getItem('theme-switcher-emulated-host')
    if (override && override.length > 0) {
      return override
    }
  } catch {
    // ignore localStorage errors
  }

  const envOverride = getDevEmulatedHost()
  if (envOverride.length > 0) {
    return envOverride
  }

  return window.location.hostname
}

function getCurrentHostState() {
  const rawHostname = getClientRawHostname()
  return {
    hostname: normalizeHostname(rawHostname),
    environment: resolveEnvironment(rawHostname)
  }
}

export const useHostNameStore = defineStore('hostname', {
  state: () => getCurrentHostState(),
})

/**
 * Determine the hostname used by the app.
 * If a developer emulated host is stored in localStorage (key: 'theme-switcher-emulated-host'),
 * return that value so the app can boot as if running on that host. This is used by the ThemeSwitcher
 * to simulate domains on localhost.
 */
function getShortHostname() {
  return normalizeHostname(getClientRawHostname())
}